import { invokeAgent as invokeAgentQuery } from "@/graphql/queries";
import { invokeCompsAgent } from "@/graphql/invokeAgent";
import { onCompsAgentResponse } from "@/graphql/subscriptions";
import { DEFAULT_MODEL_KEY, resolveModelKey } from "@/constants/modelRegistry";
import { PROCESS_STATUS } from "@/store/subscriptionCore/processConstants";
import { invokeAndSubscribe } from "@/store/subscriptionCore/subscriptionApi";
import { propertyIdToDetailsPath } from "@/utils/propertyUrlUtils";

/**
 * @param {Object} params
 * @param {string} params.propertyId
 * @param {string} params.userId
 * @param {string} params.customerId
 * @param {string} [params.modelName]
 * @param {"full"|"rerun"} [params.mode] - "rerun" rebuilds the report from the property's current comps
 * @param {Object} context
 * @returns {Promise<Object>}
 */
export async function runCompsProcess(params, context) {
  const { propertyId, userId, customerId, modelName = DEFAULT_MODEL_KEY, mode } = params;
  const resolvedModelName = resolveModelKey(modelName, DEFAULT_MODEL_KEY);
  const isRerun = mode === "rerun";
  context.setProgress({
    status: PROCESS_STATUS.INVOKING,
    message: isRerun ? "Updating comps report..." : "Starting comps generation...",
  });

  const { correlationId, payload } = await invokeAndSubscribe({
    invokeQuery: invokeAgentQuery,
    invokeVariables: invokeCompsAgent({
      propertyId,
      userId,
      customerId,
      modelName: resolvedModelName,
      mode,
    }),
    invokeResultPath: "data.invokeAgent",
    subscriptionQuery: onCompsAgentResponse,
    processTag: "Comps",
    isProcessingEvent: (event) => {
      if (event?.status === "processing") {
        context.setProgress({
          status: PROCESS_STATUS.PROCESSING,
          message: isRerun ? "Updating comps report..." : "Generating comps...",
        });
        return true;
      }
      return false;
    },
  });

  context.setCorrelationId(correlationId);

  const propertyActionPath = propertyIdToDetailsPath(propertyId);

  return {
    correlationId,
    payload,
    notification: {
      outcome: "success",
      message: isRerun ? "Comps report was updated." : "Comps were generated successfully.",
      actionRoute: propertyActionPath,
      actionLabel: propertyActionPath ? "View Property" : null,
    },
  };
}
